import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useState } from 'react';
import { BsFillPlusCircleFill } from 'react-icons/bs';
import { Link } from 'react-router-dom';

import '../../../index.css';
import { slugify } from '../../../utils/slugify';
import updateDB from '../../../utils/updateDB';
import { DesignerTag } from './GolfCourseDesign';
import CollectionSelect from '../../input/CollectionSelect';
import DesignerSelect from '../../input/DesignerSelect';

const VisitorInfo = ({
  course,
  clubCourseMapping,
  greenFees,
  reciprocalsClean,
  fetchCourse,
  user,
  editableCourses,
  editPermissions,
  username,
}) => {
  const [courseStyle, setCourseStyle] = useState(
    course ? course.course_style : '',
  );
  const [numberOfHoles, setNumberOfHoles] = useState(
    course ? course.number_holes : '',
  );
  const [par, setPar] = useState(course ? course.par : '');
  const [yardage, setYardage] = useState(course ? course.back_yardage : '');
  const [address, setAddress] = useState(course ? course.course_address : '');
  const [country, setCountry] = useState(course ? course.course_country : '');
  const [designerOriginal, setDesignerOriginal] = useState(
    course ? course.designer_original_1 : '',
  );
  const [designerContributor, setDesignerContributor] = useState('');
  const [designContributors, setDesignContributors] = useState(
    course && course.designer_contributor ? course.designer_contributor : [],
  );
  const [collections, setCollections] = useState(
    course && course.collections ? course.collections : [],
  );
  const [collection, setCollection] = useState('');

  const [openedDate, setOpenedDate] = useState(
    course ? course.opened_date : '',
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [lastEditedBy, setLastEditedBy] = useState(
    course && course.last_edited_by
      ? course.last_edited_by[course.last_edited_by.length - 1]
      : '',
  );
  const [lastEditedTime, setLastEditedTime] = useState(
    course && course.last_edited_time
      ? course.last_edited_time[course.last_edited_time.length - 1]
      : '',
  );

  const canEdit = () => {
    if (!user) return false;
    if (editPermissions === 'Advanced' || editPermissions === 'Ambassador')
      return true;
    if (
      (editPermissions === 'Basic' ||
        editPermissions === 'Ambassador' ||
        editPermissions === 'Editor') &&
      Array.isArray(editableCourses) &&
      editableCourses.includes(course.course_name)
    ) {
      return true;
    }
    return false;
  };

  const handleEditTable = () => {
    setIsEditMode(true);
  };

  const saveAllChanges = async () => {
    //console.log("saveAllChanges triggered");
    try {
      // Update course style
      await updateDB(
        'golf_courses',
        { course_style: courseStyle },
        'course_name',
        course.course_name,
      );

      // Update designer original
      await updateDB(
        'golf_courses',
        { designer_original_1: designerOriginal },
        'course_name',
        course.course_name,
      );

      // Update number of holes
      await updateDB(
        'golf_courses',
        { number_holes: numberOfHoles },
        'course_name',
        course.course_name,
      );

      // Update par
      await updateDB(
        'golf_courses',
        { par: par },
        'course_name',
        course.course_name,
      );

      // Update yardage
      await updateDB(
        'golf_courses',
        { back_yardage: yardage },
        'course_name',
        course.course_name,
      );

      // Update address
      await updateDB(
        'golf_courses',
        { course_address: address },
        'course_name',
        course.course_name,
      );

      // Update country
      await updateDB(
        'golf_courses',
        { course_country: country },
        'course_name',
        course.course_name,
      );

      // Update design contributors
      await updateDB(
        'golf_courses',
        { designer_contributor: designContributors },
        'course_name',
        course.course_name,
      );

      // Update opened date
      await updateDB(
        'golf_courses',
        { opened_date: openedDate },
        'course_name',
        course.course_name,
      );

      //updater logic
      const current_date = new Date().toISOString().slice(0, 10); // YYYY-MM-DD format
      setLastEditedBy(username);
      setLastEditedTime(current_date);

      // Ensure we have an array or start with an empty one
      const currentEditedByArray = Array.isArray(course.last_edited_by)
        ? course.last_edited_by
        : [];
      const currentEditedTimeArray = Array.isArray(course.last_edited_time)
        ? course.last_edited_time
        : [];

      // Update edited by and date
      await updateDB(
        'golf_courses',
        {
          last_edited_by: [...currentEditedByArray, username],
          last_edited_time: [...currentEditedTimeArray, current_date],
        },
        'course_name',
        course.course_name,
      );

      //console.log("All changes saved successfully!");
    } catch (error) {
      console.error('Error saving changes:', error);
    }

    setIsEditMode(false);
  };

  const handleCourseStyleChange = async (e) => {
    setCourseStyle(e.target.value);
    await updateDB(
      'golf_courses',
      { course_style: e.target.value },
      'course_name',
      course.course_name,
    );
    fetchCourse(); // refetch the course
  };

  const handleDesignerOriginalChange = async (option) => {
    const newDesigner = option ? option.value : ''; // gets the value from the selected option
    setDesignerOriginal(newDesigner);
    await updateDB(
      'golf_courses',
      { designer_original_1: newDesigner },
      'course_name',
      course.course_name,
    );
    fetchCourse(); // refetch the course
  };

  const handleNumberOfHolesChange = async (e) => {
    setNumberOfHoles(e.target.value);
    await updateDB(
      'golf_courses',
      { number_holes: e.target.value },
      'course_name',
      course.course_name,
    );
    fetchCourse(); // refetch the course
  };

  const handleParChange = async (e) => {
    setPar(e.target.value);
    await updateDB(
      'golf_courses',
      { par: e.target.value },
      'course_name',
      course.course_name,
    );
    fetchCourse(); // refetch the course
  };

  const handleAddressChange = async (e) => {
    setAddress(e.target.value);
    await updateDB(
      'golf_courses',
      { course_address: e.target.value },
      'course_name',
      course.course_name,
    );
    fetchCourse(); // refetch the course
  };

  const handleCountryChange = async (e) => {
    setCountry(e.target.value);
    await updateDB(
      'golf_courses',
      { course_country: e.target.value },
      'course_name',
      course.course_name,
    );
    fetchCourse(); // refetch the course
  };

  const handleYardageChange = async (e) => {
    setYardage(e.target.value);
    await updateDB(
      'golf_courses',
      { back_yardage: e.target.value },
      'course_name',
      course.course_name,
    );
    fetchCourse(); // refetch the course
  };

  const handleOpenedDateChange = async (e) => {
    setOpenedDate(e.target.value);
    await updateDB(
      'golf_courses',
      { opened_date: e.target.value },
      'course_name',
      course.course_name,
    );
    fetchCourse(); // refetch the course
  };

  const handleDesignerContributorChange = async (option) => {
    const newContributor = option ? option.value : '';
    setDesignerContributor(newContributor);
  };

  const handleAddContributor = async () => {
    setDesignContributors((prevContributors) => {
      if (prevContributors.length >= 7) {
        alert('No more designers can be added');
        return prevContributors;
      }
      if (designerContributor) {
        if (prevContributors.includes(designerContributor)) {
          alert('This designer is already added');
          return prevContributors;
        }
        let updatedContributors = [...prevContributors, designerContributor];
        updateDB(
          'golf_courses',
          { designer_contributor: updatedContributors },
          'course_name',
          course.course_name,
        );
        setDesignerContributor('');
        fetchCourse();
        return updatedContributors;
      } else {
        return prevContributors;
      }
    });
  };

  const handleCollectionChange = (selectedOption) => {
    if (selectedOption && selectedOption.value) {
      setCollection(selectedOption.value);
    }
  };

  const handleAddCollection = async () => {
    if (collection) {
      setCollections((prevCollections) => {
        const updatedCollections = [...prevCollections, collection];
        updateDB(
          'golf_courses',
          { collections: updatedCollections },
          'course_name',
          course.course_name,
        );
        fetchCourse();
        return updatedCollections;
      });
      setCollection(null); // Reset selected collection after adding
    }
  };

  return (
    <>
      <section id="visitorInfo" className="mt-1 w-full max-w-screen">
        <article className="w-full h-full py-6 prose max-w-none prose-sm text-left border border-gray-300 rounded-xl bg-white shadow-lg">
          <h2 className="text-center truncate px-6">
            Visitor Information:{' '}
            <span className="text-indigo-700">{course.course_name}</span>
          </h2>
          <div className="mx-auto max-w-3xl flex justify-between gap-x-2 sm:gap-x-10 px-2">
            {isEditMode ? (
              <>
                <button
                  onClick={() => setIsEditMode(!isEditMode)}
                  className="w-full py-2 px-4 bg-red-500 hover:bg-red-700 text-white font-semibold rounded-lg mr-2 flex items-center justify-center"
                >
                  <FontAwesomeIcon icon={solid('eject')} className="mr-2" />
                  Cancel
                </button>
                <button
                  onClick={saveAllChanges}
                  className="w-full py-2 px-4 bg-green-500 hover:bg-green-700 text-white font-semibold rounded-lg ml-2 flex items-center justify-center"
                >
                  <FontAwesomeIcon
                    icon={solid('floppy-disk')}
                    className="mr-2"
                  />
                  Save Changes
                </button>
              </>
            ) : canEdit() ? (
              <button
                onClick={handleEditTable}
                className="min-w-[200px] mx-auto py-2 px-4 bg-blue-500 hover:bg-blue-700 text-white font-semibold rounded-lg flex items-center justify-center"
              >
                <FontAwesomeIcon icon={solid('user-pen')} className="mr-2" />
                Edit Table
              </button>
            ) : null}
          </div>

          {lastEditedBy && lastEditedTime && (
            <p className="text-center text-xs italic px-2">
              Last edited at {lastEditedTime}
            </p>
          )}

          <table className="mx-auto text-xs sm:text-sm max-w-xl table-bordered border border-slate-500 border-spacing-1 ">
            <thead className="bg-indigo-600">
              <tr>
                <th className="text-white ">Golf Course</th>
                <th className="text-white ">{course && course.course_name}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th>Course Style</th>
                <td>
                  {isEditMode && canEdit() ? (
                    <select
                      value={courseStyle || ''}
                      onChange={handleCourseStyleChange}
                      className="block w-full pl-3 pr-10 py-2  border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">Update Course Style</option>
                      <option value="Desert">Desert</option>
                      <option value="Downland">Downland</option>
                      <option value="Forest">Forest</option>
                      <option value="Heathland">Heathland</option>
                      <option value="Inland Links">Inland Links</option>
                      <option value="Links">Links</option>
                      <option value="Moorland">Moorland</option>
                      <option value="Parkland">Parkland</option>
                    </select>
                  ) : (
                    courseStyle || ''
                  )}
                </td>
              </tr>
              <tr>
                <th>Course Designer(s)</th>
                <td>
                  {isEditMode && canEdit() ? (
                    <DesignerSelect
                      onChange={handleDesignerOriginalChange}
                      value={designerOriginal || ''}
                    />
                  ) : (
                    <>
                      {course.designer_original_1 && (
                        <DesignerTag
                          name={course.designer_original_1}
                          className="text-blue-800 underline hover:text-indigo-600"
                        />
                      )}
                      {course.designer_original_2 && (
                        <>
                          {course.designer_original_1 && ', '}
                          <DesignerTag
                            name={course.designer_original_2}
                            className="text-blue-800 underline hover:text-indigo-600"
                          />
                        </>
                      )}
                    </>
                  )}
                </td>
              </tr>
              <tr>
                <th>Design Contributors</th>
                <td>
                  {designContributors &&
                    designContributors.map((contributor, index) => (
                      <DesignerTag
                        key={index}
                        name={contributor}
                        className="inline-flex items-center rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 mr-2 mb-2 hover:bg-indigo-600 hover:text-white"
                      />
                    ))}

                  {isEditMode && canEdit() ? (
                    <div className="flex flex-row justify-start items-center">
                      <DesignerSelect
                        key={designContributors.length}
                        onChange={handleDesignerContributorChange}
                        value={
                          designerContributor
                            ? {
                                value: designerContributor,
                                label: designerContributor,
                              }
                            : ''
                        }
                      />
                      <button onClick={handleAddContributor}>
                        <BsFillPlusCircleFill
                          size={20}
                          className="ml-4 mr-10 text-green-600"
                        />
                      </button>
                    </div>
                  ) : null}
                </td>
              </tr>

              <tr>
                <th>Number of Holes</th>
                <td>
                  {isEditMode && canEdit() ? (
                    <select
                      value={numberOfHoles || 6}
                      onChange={handleNumberOfHolesChange}
                      //onBlur={handleNumberOfHolesChange}
                      className="block w-full pl-3 pr-10 py-2  border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">--Update Number of Holes--</option>
                      {[6, 9, 12, 18].map((value) => (
                        <option value={value} key={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  ) : (
                    numberOfHoles || ''
                  )}
                </td>
              </tr>
              <tr>
                <th>Par</th>
                <td>
                  {isEditMode && canEdit() ? (
                    <select
                      value={par || 75}
                      onChange={handleParChange}
                      //onBlur={handleParChange}
                      className="block w-full pl-3 pr-10 py-2  border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">--Update Par--</option>
                      {Array.from({ length: 56 }, (_, i) => i + 20).map(
                        (value) => (
                          <option value={value} key={value}>
                            {value}
                          </option>
                        ),
                      )}
                    </select>
                  ) : (
                    par || ''
                  )}
                </td>
              </tr>
              <tr>
                <th>Yardage</th>
                <td>
                  {isEditMode && canEdit() ? (
                    <div className="flex items-center">
                      <input
                        value={yardage || ''}
                        type="number"
                        min="800"
                        max="9000"
                        onChange={handleYardageChange}
                        className="block w-full pl-3 pr-10 py-2  border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  ) : (
                    yardage || ''
                  )}
                </td>
              </tr>

              <tr>
                <th>Golf Club(s)</th>
                <td>
                  {course.linked_golf_clubs &&
                    course.linked_golf_clubs.map((club, index) => (
                      <Link
                        key={index}
                        to={`/lists/golf-clubs/${slugify(club)}`}
                        className="inline-flex items-center rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 mr-2 mb-2 hover:bg-indigo-600 hover:text-white underline"
                      >
                        {club}
                      </Link>
                    ))}
                </td>
              </tr>
              <tr>
                <th>GolfLists Course Ranking</th>
                <td>
                  {(() => {
                    switch (course.course_country) {
                      case 'England':
                        return (
                          <>
                            {course && course.course_country} -{' '}
                            {course && course.england_rank_gl}
                            <br />
                            {course && course.course_county} -{' '}
                            {course && course.county_rank}
                          </>
                        );
                      case 'Scotland':
                        return (
                          <>
                            {course && course.course_country} -{' '}
                            {course && course.scotland_rank_gl}
                            <br />
                            {course && course.course_county} -{' '}
                            {course && course.county_rank}
                          </>
                        );
                      case 'Wales':
                        return (
                          <>
                            {course && course.course_country} -{' '}
                            {course && course.wales_rank_gl}
                            <br />
                            {course && course.course_county} -{' '}
                            {course && course.county_rank}
                          </>
                        );
                      case 'Northern Ireland':
                        return (
                          <>
                            {course && course.course_country} -{' '}
                            {course && course.ni_rank_gl}
                            <br />
                            {course && course.course_county} -{' '}
                            {course && course.county_rank}
                          </>
                        );
                      case 'Ireland':
                        return (
                          <>
                            {course && course.course_country} -{' '}
                            {course && course.ireland_rank_gl}
                            <br />
                            {course && course.course_county} -{' '}
                            {course && course.county_rank}
                          </>
                        );
                      default:
                        return null;
                    }
                  })()}
                </td>
              </tr>
              <tr>
                <th>Address</th>
                <td>
                  {isEditMode && canEdit() ? (
                    <input
                      value={address || ''}
                      onChange={handleAddressChange}
                      //onBlur={handleAddressChange}
                      className="block w-full border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-ellipsis"
                    />
                  ) : (
                    address || ''
                  )}
                </td>
              </tr>

              <tr>
                <th>Country</th>
                <td>
                  {course && course.course_country ? (
                    <>{course.course_country}</>
                  ) : (
                    user && (
                      <select
                        value={country || ''}
                        onChange={handleCountryChange}
                        //onBlur={handleCountryChange}
                        className="block w-full pl-3 pr-10 py-2  border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">--Update Country--</option>
                        <option value="England">England</option>
                        <option value="Ireland">Ireland</option>
                        <option value="Northern Ireland">
                          Northern Ireland
                        </option>
                        <option value="Scotland">Scotland</option>
                        <option value="Wales">Wales</option>
                      </select>
                    )
                  )}
                </td>
              </tr>
              <tr>
                <th>Contact Information</th>
                <td>
                  {course && course.office_email && (
                    <a href={`mailto:${course && course.office_email}`}>
                      Email
                    </a>
                  )}
                  <br />
                  {course && course.office_phone}
                </td>
              </tr>
              <tr>
                <th>Courses Onsite</th>
                <td>
                  {clubCourseMapping && clubCourseMapping.sibling_courses
                    ? clubCourseMapping.sibling_courses.map((course, index) => (
                        <Link
                          key={index}
                          to={`/lists/golf-courses/${slugify(course)}`}
                          className="inline-flex items-center rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 mr-2 mb-[1px] hover:bg-green-200 underline"
                        >
                          {course}
                        </Link>
                      ))
                    : null}
                </td>
              </tr>
              <tr>
                <th>
                  Course Open Year<br></br>
                  <span className="text-xs font-thin text-gray-500 italic">
                    (At Current Site)
                  </span>
                </th>
                <td>
                  {isEditMode && canEdit() ? (
                    <input
                      type="number"
                      value={openedDate || ''}
                      onChange={handleOpenedDateChange}
                      className="block w-full pl-3 pr-10 py-2  border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  ) : (
                    openedDate || ''
                  )}
                </td>
              </tr>
              <tr>
                <th>Collections</th>
                <td>
                  {collections &&
                    collections.map((collectionItem, index) => (
                      <Link
                        key={index}
                        to={`/lists/golf-courses/collections/${slugify(collectionItem)}`}
                        className="inline-flex underline items-center rounded bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800 mr-2 mb-2 hover:bg-orange-600 hover:text-white"
                      >
                        {collectionItem}
                      </Link>
                    ))}

                  {isEditMode && canEdit() ? (
                    <div className="flex flex-row justify-start items-center">
                      <CollectionSelect
                        key={collections.length}
                        onChange={handleCollectionChange}
                        value={
                          collection
                            ? {
                                value: collection,
                                label: collection,
                              }
                            : ''
                        }
                      />
                      <button onClick={handleAddCollection}>
                        <BsFillPlusCircleFill
                          size={20}
                          className="ml-4 mr-10 text-green-600"
                        />
                      </button>
                    </div>
                  ) : null}
                </td>
              </tr>
            </tbody>
          </table>
        </article>
      </section>
    </>
  );
};
export default VisitorInfo;
